import { EDTSeanceView } from '@teetsh/app/src/queries/edt/types/EDTSeanceView';
import { EdtUpdateView } from '@teetsh/app/src/queries/edt/types/edtUpdateView';
import {
  EDTEvent,
  EDTEventType,
  EmploiDuTemps,
  EventTemplate,
  EventTemplateApi,
} from '@teetsh/app/src/types/edt';
import addMinutes from '@teetsh/app/src/utils/time/addMinutes';
import deserializeDateFromServer from '@teetsh/app/src/utils/time/deserializeDateFromServer';
import differenceInMinutes from '@teetsh/app/src/utils/time/differenceInMinutes';
import formatISO from '@teetsh/app/src/utils/time/formatISO';
import parseISO from '@teetsh/app/src/utils/time/parseISO';
import serializeDateToServer from '@teetsh/app/src/utils/time/serializeDateToServer';
import startOfMinute from '@teetsh/app/src/utils/time/startOfMinute';
import { defaultToEmptyArray } from '@teetsh/app/src/queries/sequences/serializer';
import {
  deserializeItems,
  serializeItems,
} from '@teetsh/app/src/queries/serializer';
import { EdtCreateView, EdtSeanceCreateView } from './types/edtCreateView';
import emptyBo from '@teetsh/app/src/components/Bo/emptyBo';

function deserializeEventTemplate(
  eventTemplate: EventTemplateApi,
): EventTemplate {
  return {
    ...eventTemplate,
    bo: {
      competence: serializeItems(defaultToEmptyArray(eventTemplate.competence)),
      goals: serializeItems(defaultToEmptyArray(eventTemplate.goals)),
      domaine: serializeItems(defaultToEmptyArray(eventTemplate.domaine)),
      matiere: serializeItems(defaultToEmptyArray(eventTemplate.matiere)),
      cycle: eventTemplate.cycle,
    },
    attachments: eventTemplate.attachments,
    materials: serializeItems(eventTemplate.materials),
    customFields: (eventTemplate.customFields || []).map(customField => ({
      ...customField,
      items: serializeItems(customField.items),
    })),
  };
}

export function serializeEventTemplate(
  eventTemplate: EventTemplate,
): EventTemplateApi {
  const templateBo = eventTemplate.bo || emptyBo;
  return {
    ...eventTemplate,
    cycle: templateBo.cycle,
    competence: deserializeItems(templateBo.competence),
    goals: deserializeItems(templateBo.goals),
    domaine: deserializeItems(templateBo.domaine),
    matiere: deserializeItems(templateBo.matiere),
    materials: deserializeItems(eventTemplate.materials),
    attachments: eventTemplate.attachments || [],
    customFields: (eventTemplate.customFields || []).map(customField => ({
      ...customField,
      items: deserializeItems(customField.items),
    })),
  };
}

export function deserializeEDTEvent(
  event: EDTSeanceView,
  tz: string,
): EDTEvent {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const start = deserializeDateFromServer(
    startOfMinute(parseISO(event.start)),
    tz,
  );
  return {
    id: event.id,
    order: event.order,
    classroomIds: event.classroomIds,
    atelier: event.atelier,
    skipFromVolume: event.skipFromVolume,
    tag: event.tag,
    start,
    end: addMinutes(start, event.lasting),
    repartition: event.repartition || [],
    type: event.type as EDTEventType,
    title: event.title,
    eventTemplate: event.eventTemplate
      ? deserializeEventTemplate(event.eventTemplate)
      : undefined,
  };
}

export function serializeEdtSeanceCreate(
  event: EDTEvent,
  tz: string,
): EdtSeanceCreateView {
  return {
    atelier: event.atelier,
    classroomIds: event.classroomIds,
    id: event.id,
    order: event.order,
    repartition: event.repartition,
    skipFromVolume: event.skipFromVolume,
    tagId: event.tag?.id,
    title: event.title,
    type: event.type,
    start: formatISO(serializeDateToServer(event.start, tz)),
    lasting: differenceInMinutes(event.end, event.start),
    eventTemplate: event.eventTemplate
      ? serializeEventTemplate(event.eventTemplate)
      : undefined,
  };
}

export function edtUpdateSerializer(
  edt: EmploiDuTemps,
  tz: string,
): EdtUpdateView {
  return {
    id: edt.id,
    startDay: formatISO(serializeDateToServer(edt.startDay, tz)),
    endDay: formatISO(serializeDateToServer(edt.endDay, tz)),
    name: edt.name,
    schoolId: edt.schoolId,
    step: edt.step,
    subtitle: edt.subtitle,
    view: edt.view,
  };
}

export function edtCreateSerializer(
  edt: EmploiDuTemps,
  tz: string,
): EdtCreateView {
  const events = (edt.events || []).map(event =>
    serializeEdtSeanceCreate(event, tz),
  );

  return {
    ...edtUpdateSerializer(edt, tz),
    events,
  };
}

export function edtDeserializer(edt, tz: string): EmploiDuTemps {
  const events: EDTEvent[] = !edt.events
    ? []
    : edt.events.map(e => deserializeEDTEvent(e, tz));

  return {
    ...edt,
    events,
    startDay: deserializeDateFromServer(parseISO(edt.startDay), tz),
    endDay: deserializeDateFromServer(parseISO(edt.endDay), tz),
  };
}
